import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Scales, LockKey, LockKeyOpen, CheckCircle } from "@phosphor-icons/react";

export function ComplianceLockCard({ clientId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["dd-sessions", clientId], queryFn: () => api.get(`/compliance/sessions/${clientId}`).then((r) => r.data) });
  const lock = useMutation({
    mutationFn: ({ id, locked }) => api.post(`/compliance/${id}/lock`, { locked }),
    onSuccess: (_, v) => { toast.success(v.locked ? "Questionnaire locked — client answers are now frozen" : "Questionnaire unlocked — client may amend answers"); qc.invalidateQueries({ queryKey: ["dd-sessions", clientId] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const sessions = data?.sessions || [];
  if (!sessions.length) return null;
  const canLock = ["cpa", "admin"].includes(user?.role);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 mb-4" data-testid="compliance-lock-card">
      <p className="text-xs font-medium text-zinc-900 flex items-center gap-1.5 mb-0.5"><Scales size={14} /> Statutory due diligence record</p>
      <p className="text-[10px] text-zinc-400 mb-3">Lock the questionnaire when the return is filed — answers freeze into the audit file. Auto-locks when the return is marked Accepted.</p>
      <div className="space-y-2">
        {sessions.map((s) => (
          <div key={s.id} data-testid={`dd-session-${s.id}`} className="rounded-md border border-zinc-100 px-3 py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-zinc-800 truncate">{s.service_type || "Engagement"} · {s.tax_year || "—"}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">
                {s.answered}/{s.total} answered
                {s.completed_at && <span className="text-emerald-700"> · complete</span>}
                {s.locked && s.locked_by && ` · locked by ${s.locked_by}${s.locked_at ? ` on ${new Date(s.locked_at).toLocaleDateString()}` : ""}`}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {s.locked ? (
                <span className="text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-sm px-2 py-0.5 flex items-center gap-1"><LockKey size={11} /> LOCKED</span>
              ) : s.completed_at ? (
                <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-0.5 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> COMPLETE</span>
              ) : (
                <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-2 py-0.5">IN PROGRESS</span>
              )}
              {canLock && (
                <Button data-testid={`dd-lock-btn-${s.id}`} size="sm" variant="outline" className="h-7 text-[11px]" disabled={lock.isPending}
                  onClick={() => lock.mutate({ id: s.id, locked: !s.locked })}>
                  {s.locked ? <><LockKeyOpen size={12} className="mr-1" /> Unlock</> : <><LockKey size={12} className="mr-1" /> Lock</>}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
